import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import type { CityLayout } from '../world/types';
import type { SignalService } from './signalController';
import { ParkedManager } from './parkedCars';
import { RoadNet } from './roadNet';
import { TrafficSim } from './trafficSim';
import type { TrafficService } from './types';
import { VehicleGrid } from './vehicleGrid';
import { ViewTester } from './visibility';

export { createSignalSystem } from './signalController';

/**
 * Civilian traffic + parked cars (order 240, decision band: writes VehicleControls before the
 * vehicle system runs). Also owns the per-step `vehicleGrid` that peds and police share.
 */
export function createTrafficSystem(): System {
  let ctx: GameContext;
  let layout: CityLayout;
  let signals: SignalService;
  let net: RoadNet;
  let sim: TrafficSim;
  let parked: ParkedManager;
  let grid: VehicleGrid;
  let avgMs = 0;
  let populated = false;
  let started = false;
  let lastFocus: { x: number; z: number } | null = null;
  const offs: (() => void)[] = [];

  return {
    id: 'traffic',
    order: 240,
    init(c) {
      ctx = c;
      layout = c.services.get('layout');
      signals = c.services.get('signals');
      net = new RoadNet(layout);
      grid = new VehicleGrid();
      c.services.provide('vehicleGrid', grid);
      // The traffic/parked services need `vehicles` (order 300): they are created in start().
      // Until then a focus-less view tester is enough for hooks that may not be called yet.

      offs.push(
        c.events.on('collision', (e) => {
          if (!sim) return;
          const b = e.b;
          if (e.a.kind === 'vehicle') sim.onCollision(e.a.id, b && b.kind === 'vehicle' ? b.id : null, e.impulse, e.pos);
          if (b && b.kind === 'vehicle') sim.onCollision(b.id, e.a.kind === 'vehicle' ? e.a.id : null, e.impulse, e.pos);
        }),
        c.events.on('vehicleDestroyed', (e) => sim?.panicNear(e.pos, 30)),
        // The bang ~4.5 s later is the loudest thing in the game: scare drivers again, wider.
        c.events.on('vehicleExploded', (e) => sim?.explosionNear(e.pos, 40)),
        c.events.on('pedHit', (e) => sim?.panicNear(e.pos, 18)),
      );

      c.hooks.register('setTrafficDensity', (d) => {
        sim.density = Math.max(0, d);
        if (sim.density === 0) sim.clearAmbient();
        return sim.target;
      });
      // Wall-clock timings live in a hook, not in snapshot(), so snapshots stay deterministic.
      c.hooks.register('trafficPerf', () => ({ trafficMs: +avgMs.toFixed(4), pedMs: +(c.services.tryGet('peds')?.stats.pedMs ?? 0).toFixed(4) }));
      c.hooks.register('inspectTraffic', () => sim.agents());
      c.hooks.register('spawnTrafficAt', (x, z, type) => {
        const hit = net.nearestLane({ x, z });
        if (!hit) return -1;
        const lane = hit.lane;
        const v = sim.spawn({ edgeId: lane.edgeId, forward: lane.forward, lane: lane.index, t: hit.s / lane.length, specId: type });
        return v ? v.id : -1;
      });
      c.hooks.registerSnapshot('traffic', () => ({
        active: sim.active,
        target: sim.target,
        density: sim.density,
        parked: parked.active,
        spawned: sim.stats.spawned,
        despawned: sim.stats.despawned,
        stuckRecoveries: sim.stats.stuckRecoveries,
        honks: sim.stats.honks,
        poolCreated: sim.stats.poolCreated,
        poolInUse: sim.stats.poolInUse,
        holds: sim.agents().reduce<Record<string, number>>((acc, a) => {
          acc[a.hold] = (acc[a.hold] ?? 0) + 1;
          return acc;
        }, {}),
        sample: sim.agents().slice(0, 10),
      }));
    },
    start() {
      const vehicles = ctx.services.get('vehicles');
      const focus = (): { x: number; z: number } => (sim ? sim.focus() : layout.playerSpawn.pos);
      const view = new ViewTester(ctx, focus);
      sim = new TrafficSim(ctx, vehicles, net, signals, grid, view);
      // No parking on the junction-less ring-road corners: cars turn through those kerbs.
      const bends = layout.roads.nodes.filter((n) => net.bendRadius(n.id) > 0).map((n) => ({ pos: n.pos, r: net.bendRadius(n.id) + 2.5 }));
      const onBend = (p: { x: number; z: number }): boolean => bends.some((b) => Math.hypot(p.x - b.pos.x, p.z - b.pos.z) < b.r);
      parked = new ParkedManager(ctx, vehicles, layout.parkedSpots, view, focus, layout.playerSpawn.pos, onBend);
      ctx.services.provide('traffic', sim);
      ctx.services.provide('parked', parked);
    },
    fixedUpdate(dt) {
      const t0 = performance.now();
      const allVehicles = ctx.services.get('vehicles').all();
      // MITIGATION (owner: game-director, src/vehicles/vehicleSystem.ts): that system skips sleeping
      // bodies, so any driven car (the player's included) that stood still for > 0.6 s stops reacting to
      // controls. Wake driven vehicles whose controls are live; remove once vehicleSystem does it itself.
      for (let i = 0; i < allVehicles.length; i++) {
        const v = allVehicles[i]!;
        if (v.body.sleeping && v.occupant !== null && v.controller === 'player' && (v.controls.throttle !== 0 || v.controls.steer !== 0)) v.body.wake();
      }
      grid.rebuild(allVehicles, ctx.state.frame);
      // A big jump of the focus (teleport, respawn, cinematic cut) means nobody saw the old
      // surroundings go away: repopulate around the new spot immediately.
      const f = sim.focus();
      let jumped = false;
      if (lastFocus && Math.hypot(f.x - lastFocus.x, f.z - lastFocus.z) > 50) {
        populated = false;
        jumped = true;
      }
      lastFocus = { x: f.x, z: f.z };
      if (!populated) {
        const atStart = !started;
        started = true;
        // First step, after EVERY system has started: other modules' scenery cars (scaffold sandbox,
        // missions) already exist, so we populate around them instead of on top of them.
        populated = true;
        if (jumped) {
          // The old surroundings are gone: free their cars first, or they still count towards the target.
          sim.releaseFar();
          parked.releaseFar();
        }
        sim.fillInitial();
        parked.fillInitial(atStart);
        grid.rebuild(ctx.services.get('vehicles').all(), ctx.state.frame);
      }
      sim.fixedUpdate(dt);
      parked.fixedUpdate();
      avgMs += (performance.now() - t0 - avgMs) * 0.02;
      sim.stats.trafficMs = avgMs;
      sim.stats.poolCreated = sim.poolCreated;
      sim.stats.poolInUse = sim.poolInUse;
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      sim.dispose();
      parked.dispose();
    },
  };
}

export type { TrafficService };
